import { type FormValidateFn } from '@tanstack/react-form';
import { type ZodSafeParseResult } from 'zod';

export type FormError = { message: string };

export type FormValidator<TFormValue> = (
  args: Parameters<FormValidateFn<TFormValue>>[0],
) => { path: keyof TFormValue; error: FormError } | null | undefined;

export function validateFormValue<
  TFormValue,
  TSchema extends {
    safeParse: (value: TFormValue) => ZodSafeParseResult<TFormValue>;
  },
>(schema: TSchema, validators: FormValidator<TFormValue>[]) {
  return ({ formApi, value }: Parameters<FormValidateFn<TFormValue>>[0]) => {
    const zodResult = schema.safeParse(value);
    const errors: Record<string, { message: string }> = {};

    if (!zodResult.success) {
      zodResult.error.issues.forEach(issue => {
        const path = issue.path.reduce<string>((acc, segment) => {
          if (typeof segment === 'number') {
            return `${acc}[${segment}]`;
          }

          const segmentPath = String(segment);

          if (!acc) {
            return segmentPath;
          }

          return `${acc}.${segmentPath}`;
        }, '');

        if (path) {
          errors[path] = { message: issue.message };
        }
      });
    }

    for (const validator of validators) {
      const result = validator({ formApi, value });
      if (result) {
        errors[result.path as string] = result.error;
      }
    }

    return Object.keys(errors).length > 0
      ? { form: 'Invalid data', fields: errors }
      : undefined;
  };
}
